import { ActionsUnion, ResponseType } from 'utils'
import { clientCrudActionEvent } from './ClientAction'
import {
  ClearingHouse,
  Collateral,
  CollateralBasket,
  CrudDeleteEvent,
  EntityTypeEnum,
  FatFingerProductSectorLimit,
  HaircutEntry,
  HaircutEntryUpsertEvent,
  Holiday,
  Index,
  OrganisationAggregate,
  SpreadsheetMapping,
  TradingEntity,
  TradingEntityMeta,
  User,
  UserPreference,
  UserSettings
} from './generated'
import { Locale, Translation } from 'locale'

export type EntityDeleteArgs = Omit<CrudDeleteEvent, 'eventType' | 'correlationId'>

function entityDelete(entityType: EntityTypeEnum, entityId: string) {
  const args: EntityDeleteArgs = {
    entityType,
    entityId
  }
  // TODO needs proper implementation
  if (entityType === EntityTypeEnum.TRANSACTION_META) {
    // hack just to avoid the compilation error below
    entityType = EntityTypeEnum.DEAL
  }
  if (entityType === EntityTypeEnum.RESPONSE_CLUSTER_MESSAGE) {
    // hack just to avoid the compilation error below
    entityType = EntityTypeEnum.DEAL
  }
  if (entityType === EntityTypeEnum.RESET_SOTW) {
    // hack just to avoid the compilation error below
    entityType = EntityTypeEnum.DEAL
  }
  if (entityType === EntityTypeEnum.SCHEDULED_TASK_STATE) {
    // hack just to avoid the compilation error below
    entityType = EntityTypeEnum.DEAL
  }
  if (entityType === EntityTypeEnum.EGRESS_SOTW_PUBLISH) {
    // hack just to avoid the compilation error below
    entityType = EntityTypeEnum.DEAL
  }
  if (entityType === EntityTypeEnum.COMMAND) {
    // hack just to avoid the compilation error below
    entityType = EntityTypeEnum.DEAL
  }
  if (entityType === EntityTypeEnum.SOTW_START_FROM_CHECKPOINT) {
    // hack just to avoid the compilation error below
    entityType = EntityTypeEnum.DEAL
  }
  return clientCrudActionEvent('CrudDeleteEvent', args, { entityIds: [entityId], entityType })
}

/* Clearing House */

function clearingHouseUpsert(clearingHouse: ClearingHouse) {
  return clientCrudActionEvent(
    'ClearingHouseUpsertEvent',
    { entity: clearingHouse },
    { entityIds: [clearingHouse.id], entityType: EntityTypeEnum.CLEARING_HOUSE }
  )
}

/* Collateral */

function collateralUpsert(collateral: Collateral) {
  return clientCrudActionEvent(
    'CollateralUpsertEvent',
    { entity: collateral },
    { entityIds: [collateral.id], entityType: EntityTypeEnum.COLLATERAL }
  )
}

/* Collateral Basket */

function collateralBasketUpsert(collateralBasket: CollateralBasket) {
  return clientCrudActionEvent(
    'CollateralBasketUpsertEvent',
    { entity: collateralBasket },
    { entityIds: [collateralBasket.id], entityType: EntityTypeEnum.COLLATERAL_BASKET }
  )
}

/* Fat Finger Limits */

function fatFingerUpsert(fatFinger: FatFingerProductSectorLimit) {
  return clientCrudActionEvent(
    'FatFingerProductSectorLimitUpsertEvent',
    { entity: fatFinger },
    { entityIds: [fatFinger.productSectorId], entityType: EntityTypeEnum.FAT_FINGER_PRODUCT_SECTOR }
  )
}

function fatFingerDelete(fatFingerId: string) {
  return entityDelete(EntityTypeEnum.FAT_FINGER_PRODUCT_SECTOR, fatFingerId)
}

/* Haircut Matrix */

export type HaircutEntryUpsertArgs = Omit<HaircutEntryUpsertEvent, 'eventType' | 'correlationId'>

function haircutEntryUpsert(haircutEntry: HaircutEntry) {
  return clientCrudActionEvent(
    'HaircutEntryUpsertEvent',
    { entity: haircutEntry },
    { entityIds: [haircutEntry.id], entityType: EntityTypeEnum.HAIRCUT_ENTRY }
  )
}

function haircutEntryDelete(haircutEntryId: string) {
  return entityDelete(EntityTypeEnum.HAIRCUT_ENTRY, haircutEntryId)
}

/* Holiday */

function holidayUpsert(holiday: Holiday) {
  return clientCrudActionEvent(
    'HolidayUpsertEvent',
    { entity: holiday },
    { entityIds: [holiday.currency], entityType: EntityTypeEnum.HOLIDAY }
  )
}

function holidayDelete(currency: string) {
  return entityDelete(EntityTypeEnum.HOLIDAY, currency)
}

/* Index */

function indexUpsert(index: Index) {
  return clientCrudActionEvent('IndexUpsertEvent', { entity: index }, { entityIds: [index.id], entityType: EntityTypeEnum.INDEX })
}

function indexDelete(id: string) {
  return entityDelete(EntityTypeEnum.INDEX, id)
}

/* Organisation */

function organisationUpsert(organisationAgg: OrganisationAggregate) {
  return clientCrudActionEvent(
    'OrganisationUpsertEvent',
    { entity: organisationAgg },
    { entityIds: [organisationAgg.id], entityType: EntityTypeEnum.ORGANISATION }
  )
}

/* Spreadsheet Mapping */

function spreadsheetMappingUpsert(spreadsheetMapping: SpreadsheetMapping) {
  return clientCrudActionEvent(
    'SpreadsheetMappingUpsertEvent',
    { entity: spreadsheetMapping },
    { entityIds: [spreadsheetMapping.id], entityType: EntityTypeEnum.SPREADSHEET_MAPPING }
  )
}

function spreadsheetMappingDelete(mappingId: string) {
  return entityDelete(EntityTypeEnum.SPREADSHEET_MAPPING, mappingId)
}

/* Trading Entity */

function tradingEntityUpsert(tradingEntity: TradingEntity) {
  return clientCrudActionEvent(
    'TradingEntityUpsertEvent',
    { entity: tradingEntity },
    { entityIds: [tradingEntity.id], entityType: EntityTypeEnum.TRADING_ENTITY }
  )
}

/* Trading Entity Meta */

function tradingEntityMetaUpsert(tradingEntityMeta: TradingEntityMeta) {
  return clientCrudActionEvent(
    'TradingEntityMetaUpsertEvent',
    { entity: tradingEntityMeta },
    {
      entityIds: [tradingEntityMeta.id],
      entityType: EntityTypeEnum.TRADING_ENTITY_META
    }
  )
}

function tradingEntityMetaDelete(tradingEntityMetaId: string) {
  return entityDelete(EntityTypeEnum.TRADING_ENTITY_META, tradingEntityMetaId)
}

/* User */

function userUpsert(user: User) {
  return clientCrudActionEvent('UserUpsertEvent', { entity: user }, { entityIds: [user.id], entityType: EntityTypeEnum.USER })
}

/* User Preference */

function userPreferenceUpsert(userPreference: UserPreference) {
  return clientCrudActionEvent(
    'UserPreferenceUpsertEvent',
    { entity: userPreference },
    { entityIds: [userPreference.id], entityType: EntityTypeEnum.USER_PREFERENCE },
    {
      successResponse: ResponseType.GROWL,
      successMessage: Locale.translate(Translation.SC_NOTIFICATION_USER_PREFERENCE_UPDATE_SUCCESS),
      errorResponse: ResponseType.GROWL,
      errorRetry: true
    }
  )
}

function userSettingsUpsert(userSettings: UserSettings) {
  return clientCrudActionEvent(
    'UserSettingsUpsertEvent',
    { entity: userSettings },
    { entityIds: [userSettings.id], entityType: EntityTypeEnum.USER_SETTINGS }
  )
}

export const CrudActions = {
  entityDelete,
  collateralBasketUpsert,
  clearingHouseUpsert,
  collateralUpsert,
  fatFingerUpsert,
  fatFingerDelete,
  haircutEntryUpsert,
  haircutEntryDelete,
  holidayUpsert,
  holidayDelete,
  indexUpsert,
  indexDelete,
  organisationUpsert,
  spreadsheetMappingDelete,
  spreadsheetMappingUpsert,
  tradingEntityUpsert,
  tradingEntityMetaUpsert,
  tradingEntityMetaDelete,
  userUpsert,
  userPreferenceUpsert,
  userSettingsUpsert
}

export type CrudActions = ActionsUnion<typeof CrudActions>
